/**
 * Shared footer for BaseTemplate (light) and BaseTemplateBlack (dark).
 */

import {
  BRAND_NAME,
  BRAND_SITE_HOST,
  BRAND_SITE_URL,
  BRAND_HANDLE,
  BRAND_INSTAGRAM_URL,
  BRAND_TIKTOK_URL,
} from "@/lib/brandConfig";
import {
  trackBaseTemplateClick,
  type BaseTemplateAnalyticsName,
} from "@/lib/baseTemplateAnalytics";
import { Instagram } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

export type BaseTemplateFooterVariant = "light" | "dark";

function footerNavElementMeta(item: {
  to?: string;
  href?: string;
  label: string;
}): { elementId: string; href: string } {
  if (item.href?.startsWith("#") || item.href?.startsWith("mailto:")) {
    return { elementId: "footer_nav_contact", href: item.href };
  }
  if (item.to === "/portfolio") {
    return { elementId: "footer_nav_portfolio", href: item.to };
  }
  if (item.to === "/sobre-mi") {
    return { elementId: "footer_nav_sobre_mi", href: item.to };
  }
  return {
    elementId: "footer_nav_other",
    href: item.to ?? item.href ?? "",
  };
}

export interface BaseTemplateFooterProps {
  variant: BaseTemplateFooterVariant;
  language: string;
  /** e.g. "#contacto" on home with embedded form, or "mailto:…" for email. Omit to hide contact link. */
  contactHref?: string;
  /** When set, footer links fire `base_template_ui_click` (PostHog + GA4). */
  analyticsTemplate?: BaseTemplateAnalyticsName;
  countryCode?: string;
  /** Nombre de marca en footer. Por defecto BRAND_NAME (ej. Delfu Ocampo). */
  brandName?: string;
  /** Texto bajo el nombre; vacío = i18n baseTemplate.footer.tagline */
  footerTagline?: string;
  /** Edad efectiva (admin site-settings → fallback BRAND_AGE). Vacío = se omite "años". */
  brandAge?: string;
  /** false = ocultar enlace /portfolio (mismo criterio que el nav superior). */
  showPortfolioNavLink?: boolean;
  /** false = ocultar enlace /sobre-mi del footer. */
  showSobreMiNavLink?: boolean;
}

export function BaseTemplateFooter({
  variant,
  language: _language,
  contactHref,
  analyticsTemplate,
  countryCode,
  brandName = BRAND_NAME,
  footerTagline,
  brandAge,
  showPortfolioNavLink = true,
  showSobreMiNavLink = false,
}: BaseTemplateFooterProps) {
  const { t } = useTranslation();
  const light = variant === "light";
  // Edad gestionable desde el admin: si hay edad usamos la variante con "{{brandAge}} años";
  // si está vacía, la clave sin edad evita el "años" colgado.
  const age = (brandAge ?? "").trim();
  // Si la AGE está completa en site-settings (brandAge prop), se respeta:
  // preferimos la variante con edad para el tagline por defecto.
  const taglineText =
    typeof footerTagline === "string" && footerTagline.trim().length > 0
      ? footerTagline.trim()
      : age
        ? t("baseTemplate.footer.taglineWithAge", { brandAge: age })
        : t("baseTemplate.footer.tagline");

  const c = light
    ? {
        foot: "bg-white border-t border-black/[0.08]",
        brand: "text-black",
        tagline: "text-black/55",
        nav: "text-black/50 hover:text-black/80",
        divider: "border-black/[0.08]",
        muted: "text-black/45",
        iconRing:
          "border-black/15 text-black/50 hover:text-black hover:border-black/30 hover:bg-black/[0.03]",
        focus: "focus:outline-none focus-visible:ring-2 focus-visible:ring-black/25 focus-visible:ring-offset-2 focus-visible:ring-offset-white",
      }
    : {
        foot: "bg-black border-t border-white/[0.08]",
        brand: "text-white",
        tagline: "text-white/55",
        nav: "text-white/50 hover:text-white/90",
        divider: "border-white/[0.08]",
        muted: "text-white/45",
        iconRing:
          "border-white/15 text-white/55 hover:text-white hover:border-white/35 hover:bg-white/[0.06]",
        focus: "focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30 focus-visible:ring-offset-2 focus-visible:ring-offset-black",
      };

  const navItems: { to?: string; href?: string; label: string }[] = [
    ...(contactHref
      ? [
          contactHref.startsWith("#") || contactHref.startsWith("mailto:")
            ? { href: contactHref, label: t("baseTemplate.footer.contact") }
            : { to: contactHref, label: t("baseTemplate.footer.contact") },
        ]
      : []),
    ...(showPortfolioNavLink
      ? [{ to: "/portfolio" as const, label: t("baseTemplate.footer.portfolio") }]
      : []),
    ...(showSobreMiNavLink
      ? [{ to: "/sobre-mi" as const, label: t("baseTemplate.footer.aboutShort") }]
      : []),
  ];

  return (
    <footer
      className={`${c.foot} pb-[calc(2rem+env(safe-area-inset-bottom,0px))] pt-10 sm:pt-12 px-4 sm:px-6 lg:px-8`}
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col gap-10 lg:flex-row lg:items-start lg:justify-between lg:gap-12">
          <div className="text-center lg:text-left max-w-md mx-auto lg:mx-0">
            <p
              className={`font-display text-xl sm:text-2xl tracking-wide ${c.brand}`}
            >
              {brandName}
            </p>
            <p
              className={`font-body mt-2 text-sm leading-relaxed lining-nums tabular-nums font-feature-lining-nums font-feature-tabular-nums ${c.tagline}`}
              style={{ fontFeatureSettings: '"lnum" "tnum"' }}
            >
              {taglineText}
            </p>
          </div>

          <nav
            className="flex flex-wrap items-center justify-center lg:justify-end gap-x-6 gap-y-3"
            aria-label={t("baseTemplate.footer.navAria")}
          >
            {navItems.map((item) => {
              const className = `font-body text-xs font-semibold uppercase tracking-[0.14em] transition-colors ${c.nav} ${c.focus}`;
              const { elementId, href } = footerNavElementMeta(item);
              const trackNav = () => {
                if (!analyticsTemplate) return;
                trackBaseTemplateClick({
                  templateName: analyticsTemplate,
                  elementId,
                  label: item.label,
                  surface: "footer",
                  href,
                  countryCode,
                });
              };
              if (item.href) {
                return (
                  <a
                    key={item.href}
                    href={item.href}
                    className={className}
                    onClick={trackNav}
                  >
                    {item.label}
                  </a>
                );
              }
              return (
                <Link
                  key={item.to}
                  to={item.to!}
                  className={className}
                  onClick={trackNav}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>

        <div
          className={`mt-10 flex flex-col items-center gap-6 sm:flex-row sm:justify-between sm:items-center border-t ${c.divider} pt-8`}
        >
          <div className="flex items-center justify-center gap-3">
            <a
              href={BRAND_INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                if (!analyticsTemplate) return;
                trackBaseTemplateClick({
                  templateName: analyticsTemplate,
                  elementId: "footer_social_instagram",
                  label: "Instagram",
                  surface: "footer",
                  href: BRAND_INSTAGRAM_URL,
                  countryCode,
                });
              }}
              className={`flex h-10 w-10 items-center justify-center rounded-full border ui-transition duration-200 active:scale-95 touch-manipulation ${c.iconRing} ${c.focus}`}
              aria-label={`Instagram — @${BRAND_HANDLE}`}
            >
              <Instagram className="h-[18px] w-[18px]" strokeWidth={1.5} />
            </a>
            <a
              href={BRAND_TIKTOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                if (!analyticsTemplate) return;
                trackBaseTemplateClick({
                  templateName: analyticsTemplate,
                  elementId: "footer_social_tiktok",
                  label: "TikTok",
                  surface: "footer",
                  href: BRAND_TIKTOK_URL,
                  countryCode,
                });
              }}
              className={`flex h-10 w-10 items-center justify-center rounded-full border ui-transition duration-200 active:scale-95 touch-manipulation ${c.iconRing} ${c.focus}`}
              aria-label="TikTok"
            >
              <svg
                className="h-[18px] w-[18px]"
                fill="currentColor"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
              </svg>
            </a>
          </div>

          <div
            className={`flex flex-col items-center gap-1 sm:items-end text-center sm:text-right ${c.muted}`}
          >
            <a
              href={BRAND_SITE_URL}
              onClick={() => {
                if (!analyticsTemplate) return;
                trackBaseTemplateClick({
                  templateName: analyticsTemplate,
                  elementId: "footer_domain_brand",
                  label: BRAND_SITE_HOST,
                  surface: "footer",
                  href: BRAND_SITE_URL,
                  countryCode,
                });
              }}
              className={`font-body text-xs font-medium tracking-wide transition-colors ${light ? "hover:text-black/70" : "hover:text-white/70"} ${c.focus} rounded-sm`}
            >
              {BRAND_SITE_HOST}
            </a>
            <p className="font-body text-[11px] sm:text-xs leading-snug lining-nums tabular-nums">
              © <span className="tabular-nums lining-nums">{new Date().getFullYear()}</span> {brandName} ·{" "}
              {t("baseTemplate.footer.rights")}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
